import {
  useEffect,
  useMemo,
  useState
} from 'react';
import { useActiveFeatureFlags } from '@posthog/react';
import Select, { SingleValue } from 'react-select';
import SubFilterMenu from './subFilterMenu';
import customIcons from '../../utils/icons';
import useFilterStore from './filterStore';
import Button from '../buttons/baseButton';
import { FilterCategory, FrontendFilters } from '../../types/filters.type';
import { InputSearchContainer } from '../../features/streetview';
import FilterTemplate from './filterTemplate';
import type { OptionType } from '../../types/forms.type';
import { filterCategoriesByFeatureFlags, generateSubFilterMenuProps } from '../../utils/filters';
import { householdDataAccess } from '../../utils/authRoleWrap';
import useAuth from '../../utils/auth';
import { householdDataViewerRoles } from '../../types/enums';
import GetHouseholdReferenceFilter from '../../services/adapters/householdReferenceFilter';
import useRequestState from '../../hooks/useRequestState';
import { DelimiterRules } from '../../utils/pasteValueHandler';
import useFilterCount from '../../shared/hooks/useFilterCount';

const HouseholdInputSearch = householdDataAccess(InputSearchContainer);

export default function FilterMenu({ filters, onShowModal }: { filters: FilterCategory[], onShowModal: () => void }) {
  const [expandedMenu, setExpandedMenu] = useState<Record<string, boolean>>({});
  const [selectedFilterGroup, setSelectedFilterGroup] = useState<string>('');
  const [searchResult, setSearchResult] = useState<string | null>(null);
  const { clearAllFilters } = useFilterStore();
  const filterCount = useFilterCount();
  const auth = useAuth();
  const activeFeatureFlags = useActiveFeatureFlags();
  const visibleFilterCategories = useMemo(
    () => filterCategoriesByFeatureFlags(filters, activeFeatureFlags),
    [filters, activeFeatureFlags],
  );

  const toggleSubmenu = (menuItem: string) => {
    if (selectedFilterGroup === menuItem) {
      // clicking the same item will collapse the submenu
      setSelectedFilterGroup('');
      // reset expanded menu states
      setExpandedMenu({});
    } else {
      // Switch parent filter topic
      setSelectedFilterGroup(menuItem);
      // Make sure submenu is visible
      const newExpandedState: { [key: string]: boolean } = {};

      visibleFilterCategories.forEach((key) => {
        newExpandedState[key.displayName] = false;
      });

      newExpandedState[menuItem] = true;
      setExpandedMenu(newExpandedState);
    }
  };
  const filterGroupNames: string[] = visibleFilterCategories.map((category) => category.displayName);
  const subMenuData = selectedFilterGroup
    ? visibleFilterCategories.find((category) => category.displayName === selectedFilterGroup)?.subcategories || []
    : [];

  const searchableFilters = generateSubFilterMenuProps(visibleFilterCategories);
  const findSearchedFilter = (searchValue: SingleValue<OptionType>) => {
    setSearchResult(searchValue?.value || null);
    const selectedFilter = searchableFilters.find((filter) => filter.value === searchValue?.value);
    if (selectedFilter?.category) {
      setSelectedFilterGroup(selectedFilter.category);
      setExpandedMenu({ [selectedFilter.category]: true });
    }
    if (!searchValue) {
      setSelectedFilterGroup('');
      setExpandedMenu({});
    }
    setTimeout(() => {
      setSearchResult(null);
    }, 6000);
  };
  const {
    data: householdReferenceFilter,
    handleRequest,
  } = useRequestState<FrontendFilters>();
  useEffect(() => {
    if (householdDataViewerRoles.includes(auth.role)) {
      const householdReferenceAdapter = new GetHouseholdReferenceFilter();
      void handleRequest(householdReferenceAdapter.adaptResponse());
    }
  }, [auth.role]);
  return (
    <div
      id="filter-menu-container"
      className="flex flex-col lg:max-w-fit h-screen lg:flex-row"
    >
      <div className="flex flex-col bg-pipbg lg:w-80 lg:h-full border-t lg:border-l border-pipgrey-border order-last">
        <div id="search-container" className="relative p-2">
          <div className="flex items-center justify-between ">
            <div id="filter-summary-container" className="flex items-center m-2">
              <h2 className="font-semibold me-2">Filters</h2>
              <button
                type="button"
                id="filter-count-show-modal"
                className="text-secondary chip-green chip cursor-pointer"
                onClick={onShowModal}
              >
                {`${filterCount} applied`}
              </button>
            </div>
            <Button
              id="clear-filters"
              className="text-sm text-pipstatus-alert hover:text-piptext-error transition-colors"
              label="Clear all"
              onClick={clearAllFilters}
            />
          </div>
          <label htmlFor="filter-search" className="flex flex-row items-center w-full">
            <span className="sr-only">Search Filters</span>
            <div className="p-2 absolute">
              <span>{customIcons.searchIcon}</span>
            </div>
            <Select
              inputId="filter-search"
              options={searchableFilters}
              className="font-semibold text-lg w-full"
              placeholder="Search for a filter..."
              isSearchable
              isClearable
              onChange={findSearchedFilter}
              classNames={{
                input: () => '!pl-4',
                singleValue: () => '!pl-4',
                control: ({ isFocused }) => `!flex !p-0 input-default ${isFocused ? 'input-focused shadow-[#008849_0px_0px_0px_1px] border-transparent' : 'shadow-none'} `,
                option: ({ isSelected }) => `search-filter-option ${isSelected ? 'font-bold hover:bg-pipbg-subtle' : 'text-secondary'}`,
                placeholder: () => '!pl-4',
                indicatorSeparator: () => 'hidden',
                dropdownIndicator: () => 'hidden',
              }}
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: () => null,
              }}
              styles={{ // keep basic default styles so not all custom
                control: () => ({}),
                option: () => ({}),
              }}
            />
          </label>
        </div>
        <div id="filter-menu-scroll-container" className="overflow-y-auto">
          <div id="filtergroup-conatiner" className="flex flex-col items-start w-full">
            {filterGroupNames.map((filterGroupName) => (
              <div
                id={`${filterGroupName}-menu-item-container`}
                key={filterGroupName}
                className="flex flex-col w-full first:border-t"
              >
                <div className="flex flex-row items-center px-4 w-full h-10 border-b border-pipgrey-border">
                  <button
                    className="flex flex-row space-x-2 items-center"
                    onClick={() => toggleSubmenu(filterGroupName)}
                    type="button"
                    aria-expanded={expandedMenu[filterGroupName] ? 'true' : 'false'}
                    aria-controls={`submenu-${filterGroupName.replace(/\s+|&/g, '-').trim()}`}
                  >
                    <i className={`text-primary ${expandedMenu[filterGroupName] ? 'fas fa-chevron-left' : 'fas fa-chevron-right'}`} />
                    <span className={`${expandedMenu[filterGroupName] ? 'font-bold' : 'font-normal'}`}>{filterGroupName}</span>
                  </button>
                </div>

                {selectedFilterGroup === filterGroupName && (
                  <div
                    id="mobile-filter-menu"
                    className="w-full lg:hidden bg-gray-100 max-h-64 overflow-y-auto"
                  >
                    <SubFilterMenu
                      menuData={subMenuData}
                      title={selectedFilterGroup}
                      searchResult={searchResult}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-auto">
            <div className="p-4">
              {householdReferenceFilter && (
                <HouseholdInputSearch
                  placement="top"
                  label={householdReferenceFilter.displayName}
                  labelId={householdReferenceFilter.id}
                  title=""
                  placeholder={householdReferenceFilter.placeholder || ''}
                  searchOptions={householdReferenceFilter.filterValues}
                  delimiterRules={DelimiterRules.SpacesAreDelimiter}
                />
              )}
            </div>
            <FilterTemplate />
          </div>
        </div>
      </div>

      <div
        id="desktop-filter-menu"
        className={[
          'bg-pipbg',
          'hidden',
          'lg:block',
          'border-l',
          'border-pipgrey-border',
          'h-full',
          'overflow-y-auto',
          'transition-all',
          'duration-500',
          'ease-in-out',
          selectedFilterGroup ? 'w-80 opacity-100 visible' : 'w-0 opacity-0 invisible'
        ].join(' ')}
      >
        <SubFilterMenu
          menuData={subMenuData}
          title={selectedFilterGroup}
          searchResult={searchResult}
        />
      </div>
    </div>
  );
}
